import { useEffect, useMemo, useState } from "react";
import { Loader2, Wand2, Sparkles, Search, Download, RefreshCw, CheckCircle2, AlertTriangle, ShieldAlert, Clock, Coins, RotateCcw } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import RichContent from "../public/RichContent";

const ENTITY_ACTIONS = {
  product: [
    { value: "description", label: "Product Descriptions" },
    { value: "product_info", label: "Product Information" },
    { value: "meta_description", label: "Meta Descriptions" },
    { value: "faqs", label: "FAQs" },
    { value: "alt_text", label: "Image ALT Text" },
  ],
  category: [
    { value: "category_intro", label: "Category Introductions" },
    { value: "buying_guide", label: "Buying Guides" },
    { value: "meta_description", label: "Meta Descriptions" },
    { value: "faqs", label: "FAQs" },
  ],
  brand: [
    { value: "brand_description", label: "Brand Descriptions" },
    { value: "meta_description", label: "Meta Descriptions" },
    { value: "faqs", label: "FAQs" },
  ],
};

const STATUS_META = {
  pending_review: { label: "Pending Review", cls: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  approved: { label: "Approved", cls: "bg-green-500/20 text-green-300 border-green-500/30" },
  rejected: { label: "Rejected", cls: "bg-white/10 text-white/50 border-white/15" },
  failed: { label: "Failed", cls: "bg-red-500/20 text-red-300 border-red-500/30" },
  skipped: { label: "Skipped", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
};

const scoreCls = (s) => s == null ? "text-white/30" : s >= 80 ? "text-green-400" : s >= 50 ? "text-yellow-400" : "text-red-400";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function BulkAiTools() {
  const [entityType, setEntityType] = useState("product");
  const [action, setAction] = useState("description");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | manual | ai | empty
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [overwrite, setOverwrite] = useState(false);

  const [estimate, setEstimate] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [batchId, setBatchId] = useState(null);
  const [gens, setGens] = useState(null);
  const [report, setReport] = useState(null);
  const [reviewFilter, setReviewFilter] = useState("all");
  const [reviewItem, setReviewItem] = useState(null); // {gen, edited}
  const [acting, setActing] = useState(false);

  const actions = ENTITY_ACTIONS[entityType] || [];

  useEffect(() => {
    // keep action valid for entity type
    if (!actions.find((a) => a.value === action)) setAction(actions[0]?.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType]);

  const loadItems = async () => {
    setLoading(true); setSelected(new Set());
    try {
      const { data } = await api.get(`/admin/seo/bulk-items`, { params: { entity_type: entityType, action, search } });
      if (data.error) { toast.error(data.error); setItems([]); }
      else setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (actions.find((a) => a.value === action)) loadItems(); /* eslint-disable-next-line */ }, [entityType, action]);

  const visible = useMemo(() => items.filter((it) => {
    if (filter === "manual") return it.has_manual_content;
    if (filter === "ai") return it.has_ai_content;
    if (filter === "empty") return !it.has_manual_content && !it.has_ai_content;
    return true;
  }), [items, filter]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(visible.map((i) => i.id)));
  const clearSel = () => setSelected(new Set());

  const openConfirm = async () => {
    if (selected.size === 0) { toast.error("Select at least one item."); return; }
    try {
      const { data } = await api.post("/admin/seo/bulk-estimate", { entity_type: entityType, action, item_ids: [...selected], overwrite });
      setEstimate(data); setShowConfirm(true);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const runGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/seo/bulk-generate", { entity_type: entityType, action, item_ids: [...selected], overwrite });
      if (data.error) { toast.error(data.error); return; }
      setBatchId(data.batch_id); setGens(data.generations || []); setReport(data.report);
      setShowConfirm(false);
      toast.success(`Generated ${data.report.pending_review} item(s) — review and approve below.`);
      loadItems();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const refreshBatch = async (bid = batchId) => {
    if (!bid) return;
    const { data } = await api.get("/admin/seo/bulk-report", { params: { batch_id: bid } });
    setGens(data.generations || []); setReport(data.report);
  };

  const doApprove = async (gen, edited) => {
    setActing(true);
    try {
      const { data } = await api.post(`/admin/seo/ai-generations/${gen.generation_id}/approve`, { edited });
      if (!data.ok) { toast.error(data.error || "Approve failed."); return; }
      toast.success(data.detail || "Approved.");
      setReviewItem(null); await refreshBatch(); loadItems();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setActing(false); }
  };

  const doReject = async (gen) => {
    setActing(true);
    try {
      await api.post(`/admin/seo/ai-generations/${gen.generation_id}/reject`);
      toast.success("Rejected."); setReviewItem(null); await refreshBatch();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setActing(false); }
  };

  const retryFailed = async () => {
    setActing(true);
    try {
      const { data } = await api.post("/admin/seo/bulk-retry", { batch_id: batchId });
      setGens(data.generations || []); setReport(data.report);
      toast.success(`Retried ${data.retried} failed item(s).`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setActing(false); }
  };

  const downloadReport = () => {
    const rows = [["Item", "Type", "Content", "Status", "Reason", "Model", "Reviewed By", "Reviewed At"]];
    (gens || []).forEach((g) => rows.push([g.entity_name, g.entity_type, g.action_label || g.action, STATUS_META[g.status]?.label || g.status, (g.reason || "").replace(/,/g, ";"), g.model || "", g.reviewed_by || "", g.reviewed_at || ""]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "")}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `ai-bulk-report-${batchId}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const reviewVisible = (gens || []).filter((g) => reviewFilter === "all" ? true : g.status === reviewFilter);

  return (
    <div className="space-y-6" data-testid="bulk-ai-tools">
      <div className="rounded-xl border border-white/10 bg-black/20 p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-cinzel text-white flex items-center gap-2"><Wand2 className="h-5 w-5 text-gold" /> Bulk AI Content Tools</h2>
            <p className="text-xs text-white/50 mt-1">Generate content for many items at once. Nothing goes live until you review & approve it. Uses verified database data only.</p>
          </div>
        </div>

        {/* Selectors */}
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-white/60">Entity Type</Label>
            <select value={entityType} onChange={(e) => setEntityType(e.target.value)} className="cf-select w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm" data-testid="bulk-entity-type">
              <option value="product">Products</option>
              <option value="category">Categories</option>
              <option value="brand">Brands</option>
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-white/60">Content Type</Label>
            <select value={action} onChange={(e) => setAction(e.target.value)} className="cf-select w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm" data-testid="bulk-action-type">
              {actions.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-white/60">Filter</Label>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="cf-select w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm" data-testid="bulk-filter">
              <option value="all">All items</option>
              <option value="manual">Has manual content</option>
              <option value="ai">Has AI content</option>
              <option value="empty">No content yet</option>
            </select>
          </div>
        </div>

        {/* Search + bulk-select controls */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-white/40" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadItems()} placeholder="Search by name…" className="pl-8 bg-black/30 border-white/15" data-testid="bulk-search" />
          </div>
          <Button variant="outline" size="sm" onClick={loadItems} data-testid="bulk-search-btn"><Search className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={selectAll} data-testid="bulk-select-all">Select all ({visible.length})</Button>
          <Button variant="outline" size="sm" onClick={clearSel} data-testid="bulk-clear-sel">Clear</Button>
        </div>

        {/* Item list */}
        <div className="rounded-lg border border-white/10 overflow-hidden">
          <div className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-3 px-3 py-2 text-[11px] uppercase tracking-wider text-white/40 border-b border-white/10 bg-white/5">
            <span></span><span>Item</span><span className="text-center">Content</span><span className="text-center">SEO</span><span className="text-center">Last Gen</span>
          </div>
          {loading ? (
            <div className="p-6 text-center text-white/40 text-sm flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : visible.length === 0 ? (
            <div className="p-6 text-center text-white/40 text-sm" data-testid="bulk-empty">No items found. {items.length === 0 ? "This catalogue may be empty for the selected type." : "Adjust your filter or search."}</div>
          ) : visible.map((it) => (
            <label key={it.id} className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-3 px-3 py-2.5 items-center border-b border-white/5 hover:bg-white/5 cursor-pointer text-sm" data-testid={`bulk-item-${it.id}`}>
              <Checkbox checked={selected.has(it.id)} onCheckedChange={() => toggle(it.id)} data-testid={`bulk-check-${it.id}`} />
              <div className="min-w-0">
                <p className="text-white/90 truncate">{it.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {it.has_manual_content && <Badge variant="outline" className="text-[10px] border-blue-400/40 text-blue-300">Manual</Badge>}
                  {it.has_ai_content && <Badge variant="outline" className="text-[10px] border-gold/40 text-gold"><Sparkles className="h-2.5 w-2.5 mr-0.5" />AI</Badge>}
                  {!it.has_manual_content && !it.has_ai_content && <span className="text-[10px] text-white/35">empty</span>}
                  {it.existing_preview && <span className="text-[10px] text-white/35 truncate hidden md:inline">· {it.existing_preview}</span>}
                </div>
              </div>
              <div className="text-center"><span className={`text-xs font-semibold ${scoreCls(it.content_score)}`} title="Content SEO score">{it.content_score ?? "—"}</span></div>
              <div className="text-center"><span className={`text-xs font-semibold ${scoreCls(it.seo_score)}`} title="Technical SEO score">{it.seo_score ?? "—"}</span></div>
              <div className="text-center text-[11px] text-white/45">{fmtDate(it.last_generated)}</div>
            </label>
          ))}
        </div>

        {/* Overwrite + generate */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-start gap-2 text-sm max-w-md" data-testid="bulk-overwrite-wrap">
            <Switch checked={overwrite} onCheckedChange={setOverwrite} data-testid="bulk-overwrite" className="mt-0.5" />
            <span>
              <span className="text-white/85">Allow overwrite of existing content</span>
              {overwrite ? (
                <span className="block text-[11px] text-red-400 flex items-center gap-1 mt-0.5"><ShieldAlert className="h-3 w-3" /> Existing manual & AI content for selected items will be replaced by the new AI content once approved.</span>
              ) : (
                <span className="block text-[11px] text-white/45 mt-0.5">Off (recommended). Items that already have content are skipped.</span>
              )}
            </span>
          </label>
          <div className="text-sm text-white/60"><b className="text-gold" data-testid="bulk-selected-count">{selected.size}</b> selected</div>
        </div>
        <Button className="cf-gold-btn gap-2" onClick={openConfirm} disabled={selected.size === 0 || generating} data-testid="bulk-generate-btn">
          <Wand2 className="h-4 w-4" /> Generate for {selected.size} item(s)
        </Button>
      </div>

      {/* Results / review */}
      {report && (
        <div className="rounded-xl border border-white/10 bg-black/20 p-5 space-y-4" data-testid="bulk-results">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-cinzel text-white">Generation Results</h3>
            <div className="flex gap-2">
              {report.failed > 0 && <Button variant="outline" size="sm" className="gap-1" onClick={retryFailed} disabled={acting} data-testid="bulk-retry-failed"><RotateCcw className="h-3.5 w-3.5" /> Retry failed ({report.failed})</Button>}
              <Button variant="outline" size="sm" className="gap-1" onClick={downloadReport} data-testid="bulk-download-report"><Download className="h-3.5 w-3.5" /> Download report</Button>
              <Button variant="ghost" size="sm" onClick={() => refreshBatch()} data-testid="bulk-refresh"><RefreshCw className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="bulk-report-stats">
            <Stat label="Pending Review" value={report.pending_review} tone="warn" />
            <Stat label="Approved" value={report.approved} tone="ok" />
            <Stat label="Rejected" value={report.rejected} />
            <Stat label="Failed" value={report.failed} tone={report.failed ? "err" : undefined} />
            <Stat label="Skipped" value={report.skipped} sub={report.manual_skipped ? `${report.manual_skipped} manual` : ""} />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-xs text-white/50">Filter:</Label>
            <select value={reviewFilter} onChange={(e) => setReviewFilter(e.target.value)} className="cf-select bg-black/30 border border-white/15 rounded-md px-2 py-1 text-xs" data-testid="review-filter">
              <option value="all">All</option>
              <option value="pending_review">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="failed">Failed</option>
              <option value="skipped">Skipped</option>
            </select>
          </div>

          <div className="space-y-2">
            {reviewVisible.map((g) => (
              <div key={g.generation_id} className="rounded-lg border border-white/10 bg-white/5 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`review-row-${g.generation_id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-white/90 truncate">{g.entity_name} <span className="text-white/40 text-xs">· {g.action_label || g.action}</span></p>
                  {g.reason && <p className="text-[11px] text-white/45">{g.reason}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-[10px] ${STATUS_META[g.status]?.cls}`}>{STATUS_META[g.status]?.label || g.status}</Badge>
                  {g.status === "pending_review" && (
                    <Button size="sm" variant="outline" className="h-7 gap-1 border-gold/40 text-gold" onClick={() => setReviewItem({ gen: g, edited: initialEdited(g) })} data-testid={`review-open-${g.generation_id}`}>Review</Button>
                  )}
                </div>
              </div>
            ))}
            {reviewVisible.length === 0 && <p className="text-white/40 text-sm py-4 text-center">No items for this filter.</p>}
          </div>
        </div>
      )}

      {/* Estimate / confirm dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent data-testid="bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5 text-gold" /> Confirm AI Generation</DialogTitle>
            <DialogDescription>Review the estimate before generating. Content will be staged for your review and will NOT go live until you approve it.</DialogDescription>
          </DialogHeader>
          {estimate && (
            <div className="grid grid-cols-2 gap-3 py-2">
              <EstTile icon={CheckCircle2} label="Items selected" value={estimate.selected} />
              <EstTile icon={Wand2} label="Will generate" value={estimate.to_process} />
              <EstTile icon={Coins} label="Est. AI credits" value={estimate.credits} />
              <EstTile icon={Clock} label="Est. time" value={`~${Math.max(1, Math.round(estimate.est_seconds / 60)) } min`} sub={`${estimate.est_seconds}s`} />
              {estimate.skipped_existing > 0 && (
                <div className="col-span-2 text-[11px] text-sky-300 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {estimate.skipped_existing} item(s) already have content and will be skipped {overwrite ? "(overwrite is ON so they WILL be processed)" : "(enable overwrite to include them)"}.</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancel</Button>
            <Button className="cf-gold-btn gap-2" onClick={runGenerate} disabled={generating || (estimate && estimate.to_process === 0)} data-testid="bulk-confirm-generate">
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Generate now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={!!reviewItem} onOpenChange={(o) => !o && setReviewItem(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-gold" /> Review: {reviewItem?.gen.entity_name}</DialogTitle>
            <DialogDescription>{reviewItem?.gen.action_label} — generated from verified data only. Edit if needed, then approve to make it live, or reject to discard.</DialogDescription>
          </DialogHeader>
          {reviewItem && <ReviewBody item={reviewItem} setItem={setReviewItem} />}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => doReject(reviewItem.gen)} disabled={acting} data-testid="review-reject">Reject</Button>
            <Button className="cf-gold-btn" onClick={() => doApprove(reviewItem.gen, reviewItem.edited)} disabled={acting} data-testid="review-approve">
              {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Approve & Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function initialEdited(g) {
  if (g.action === "faqs") return g.faqs || [];
  if (g.action === "alt_text") return g.alt_items || [];
  return g.proposed || "";
}

function ReviewBody({ item, setItem }) {
  const g = item.gen;
  if (g.action === "faqs") {
    const faqs = item.edited || [];
    return (
      <div className="space-y-3" data-testid="review-faqs">
        {faqs.length === 0 ? <p className="text-white/50 text-sm">No FAQs generated.</p> : faqs.map((f, i) => (
          <div key={i} className="rounded-lg border border-white/10 p-3">
            <Input value={f.question} onChange={(e) => setItem({ ...item, edited: faqs.map((x, j) => j === i ? { ...x, question: e.target.value } : x) })} className="bg-black/30 border-white/15 mb-2 font-medium" />
            <RichContent html={f.answer} className="text-sm" />
          </div>
        ))}
        <p className="text-[11px] text-white/40">These will be appended to existing FAQs (existing ones kept).</p>
      </div>
    );
  }
  if (g.action === "alt_text") {
    const alts = item.edited || [];
    return (
      <div className="space-y-2" data-testid="review-alt">
        {alts.map((a, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs text-white/40 w-16">Image {a.index + 1}</span>
            <Input value={a.proposed} onChange={(e) => setItem({ ...item, edited: alts.map((x, j) => j === i ? { ...x, proposed: e.target.value } : x) })} className="bg-black/30 border-white/15 flex-1" />
          </div>
        ))}
      </div>
    );
  }
  const isHtml = g.is_html;
  return (
    <div className="space-y-4">
      {g.existing && stripText(g.existing) && (
        <div>
          <p className="text-[11px] uppercase tracking-wider text-white/40 mb-1">Current content {g.overwrite ? "(will be replaced)" : ""}</p>
          <div className="rounded-lg border border-white/10 bg-black/30 p-3 max-h-40 overflow-y-auto"><RichContent html={isHtml ? g.existing : `<p>${g.existing}</p>`} className="text-sm" /></div>
        </div>
      )}
      <div>
        <p className="text-[11px] uppercase tracking-wider text-gold mb-1">Proposed (editable)</p>
        <Textarea rows={9} value={item.edited} onChange={(e) => setItem({ ...item, edited: e.target.value })} className="bg-black/30 border-white/15 font-mono text-xs" data-testid="review-proposed" />
        <div className="mt-2 rounded-lg border border-white/10 bg-black/20 p-3"><RichContent html={isHtml ? item.edited : `<p>${item.edited}</p>`} className="text-sm" /></div>
      </div>
    </div>
  );
}

const stripText = (h) => (typeof h === "string" ? h : "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

function Stat({ label, value, tone, sub }) {
  const color = tone === "warn" ? "text-amber-400" : tone === "ok" ? "text-green-400" : tone === "err" ? "text-red-400" : "text-white";
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-center">
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-[11px] text-white/50">{label}</p>
      {sub && <p className="text-[10px] text-white/35">{sub}</p>}
    </div>
  );
}

function EstTile({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3">
      <div className="flex items-center gap-2 text-white/50 text-xs"><Icon className="h-4 w-4" /> {label}</div>
      <p className="text-xl font-bold text-white mt-1">{value}{sub && <span className="text-[11px] text-white/40 font-normal ml-1">{sub}</span>}</p>
    </div>
  );
}
